import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { projectService } from '../services/projectService';
import useLanguage from '../hooks/useLanguage';
import { getLocalizedProject } from '../utils/multilingual';
import { safeRender } from '../utils/safeRender';
import './ProjectsPage.css';

const ProjectsPage = () => {
  const { language, changeLanguage } = useLanguage();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    year: '',
    search: '',
    page: 1,
    limit: 12
  });
  const [pagination, setPagination] = useState({});
  const [years, setYears] = useState([]);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    loadProjects();
  }, [filters]);

  const loadInitialData = async () => {
    try {
      const yearsResponse = await projectService.getAvailableYears();
      setYears(yearsResponse.data || []);
    } catch (err) {
      console.error('Error loading initial data:', err);
    }
  };

  const loadProjects = async () => {
    setLoading(true);
    try {
      const response = await projectService.getProjects(filters);
      setProjects(response.data.projects || []);
      setPagination(response.data.pagination || {});
    } catch (err) {
      setError('Greška pri učitavanju projekata');
      console.error('Error loading projects:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // Reset to first page when filters change
    }));
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    // Search is already handled by useEffect when filters.search changes
  };

  const clearFilters = () => {
    setFilters({
      year: '',
      search: '',
      page: 1,
      limit: 12
    });
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };


  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);

    const localeMap = {
      'SR': 'sr-RS',
      'EN': 'en-US',
      'DE': 'de-DE'
    };

    const locale = localeMap[language] || 'sr-RS';

    return date.toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long'
    });
  };

  const content = {
    SR: {
      nav: {
        home: 'Početna',
        products: 'Proizvodi',
        services: 'Usluge',
        projects: 'Projekti',
        about: 'O nama',
        ecology: 'Ekologija',
        contact: 'Kontakt'
      },
      hero: {
        title: 'Nasi projekti',
        subtitle: 'Naš cilj je da budući projekti koje realizujemo prikažu kvalitet, pažljivo odabrane detalje i usklađene boje.'
      },
      filters: {
        title: 'Filteri',
        search: 'Pretraga',
        searchPlaceholder: 'Pretraži projekte...',
        year: 'Godina realizacije',
        allYears: 'Sve godine',
        clearFilters: 'Obriši filtere'
      },
      buttons: {
        search: 'Pretraži',
        retry: 'Pokušaj ponovo',
        viewDetails: 'Pogledaj detalje'
      },
      messages: {
        loading: 'Učitavanje projekata...',
        error: 'Greška pri učitavanju',
        noResults: 'Nema rezultata',
        noResultsDescription: 'Nije pronađen nijedan projekat koji odgovara vašim kriterijumima.',
        foundProjects: 'Pronađeno',
        projects: 'projekata',
        fromYear: 'iz',
        year: 'godine',
        forSearch: 'za',
        client: 'Klijent'
      },
      pagination: {
        previous: 'Prethodna',
        next: 'Sledeća',
        page: 'Strana',
        of: 'od'
      },
      footer: {
        contact: {
          title: 'Kontakt informacije',
          address: 'Gandijeva 235/13, 11073 Beograd',
          phone: '+381 62 213 492',
          email: 'constructions@mtc.co.rs',
          workingHours: 'Ponedeljak-Petak: 08:00-16:00'
        },
        quickLinks: {
          title: 'Brzi linkovi',
          products: 'Proizvodi',
          services: 'Usluge',
          projects: 'Projekti',
          contact: 'Kontakt'
        },
        social: {
          title: 'Pratite nas',
          facebook: 'Facebook',
          instagram: 'Instagram',
          linkedin: 'LinkedIn'
        },
        certificates: {
          title: 'Sertifikati i partneri'
        }
      }
    },
    EN: {
      nav: {
        home: 'Home',
        products: 'Products',
        services: 'Services',
        projects: 'Projects',
        about: 'About',
        ecology: 'Ecology',
        contact: 'Contact'
      },
      hero: {
        title: 'Our projects',
        subtitle: 'Our goal is that the future projects we realize demonstrate quality, carefully selected details and coordinated colors.'
      },
      filters: {
        title: 'Filters',
        search: 'Search',
        searchPlaceholder: 'Search projects...',
        year: 'Completion year',
        allYears: 'All years',
        clearFilters: 'Clear filters'
      },
      buttons: {
        search: 'Search',
        retry: 'Try again',
        viewDetails: 'View details'
      },
      messages: {
        loading: 'Loading projects...',
        error: 'Error loading',
        noResults: 'No results',
        noResultsDescription: 'No projects found matching your criteria.',
        foundProjects: 'Found',
        projects: 'projects',
        fromYear: 'from',
        year: 'year',
        forSearch: 'for',
        client: 'Client'
      },
      pagination: {
        previous: 'Previous',
        next: 'Next',
        page: 'Page',
        of: 'of'
      },
      footer: {
        contact: {
          title: 'Contact Information',
          address: 'Gandijeva 235/13, 11073 Belgrade',
          phone: '+381 62 213 492',
          email: 'constructions@mtc.co.rs',
          workingHours: 'Monday-Friday: 08:00-16:00'
        },
        quickLinks: {
          title: 'Quick Links',
          products: 'Products',
          services: 'Services',
          projects: 'Projects',
          contact: 'Contact'
        },
        social: {
          title: 'Follow Us',
          facebook: 'Facebook',
          instagram: 'Instagram',
          linkedin: 'LinkedIn'
        },
        certificates: {
          title: 'Certificates & Partners'
        }
      }
    },
    DE: {
      nav: {
        home: 'Startseite',
        products: 'Produkte',
        services: 'Dienstleistungen',
        projects: 'Projekte',
        about: 'Über uns',
        ecology: 'Ökologie',
        contact: 'Kontakt'
      },
      hero: {
        title: 'Unsere Projekte',
        subtitle: 'Unser Ziel ist es, dass die zukünftigen Projekte, die wir realisieren, Qualität, sorgfältig ausgewählte Details und abgestimmte Farben zeigen.'
      },
      filters: {
        title: 'Filter',
        search: 'Suche',
        searchPlaceholder: 'Projekte suchen...',
        year: 'Fertigstellungsjahr',
        allYears: 'Alle Jahre',
        clearFilters: 'Filter löschen'
      },
      buttons: {
        search: 'Suchen',
        retry: 'Erneut versuchen',
        viewDetails: 'Details ansehen'
      },
      messages: {
        loading: 'Projekte werden geladen...',
        error: 'Fehler beim Laden',
        noResults: 'Keine Ergebnisse',
        noResultsDescription: 'Keine Projekte gefunden, die Ihren Kriterien entsprechen.',
        foundProjects: 'Gefunden',
        projects: 'Projekte',
        fromYear: 'aus',
        year: 'Jahr',
        forSearch: 'für',
        client: 'Kunde'
      },
      pagination: {
        previous: 'Vorherige',
        next: 'Nächste',
        page: 'Seite',
        of: 'von'
      },
      footer: {
        contact: {
          title: 'Kontaktinformationen',
          address: 'Gandijeva 235/13, 11073 Belgrad',
          phone: '+381 62 213 492',
          email: 'constructions@mtc.co.rs',
          workingHours: 'Montag-Freitag: 08:00-16:00'
        },
        quickLinks: {
          title: 'Schnelllinks',
          products: 'Produkte',
          services: 'Dienstleistungen',
          projects: 'Projekte',
          contact: 'Kontakt'
        },
        social: {
          title: 'Folgen Sie uns',
          facebook: 'Facebook',
          instagram: 'Instagram',
          linkedin: 'LinkedIn'
        },
        certificates: {
          title: 'Zertifikate und Partner'
        }
      }
    }
  };

  const currentContent = content[language];

  return (
    <div className="projects-page">
      {/* Header Section */}
      <Header 
        language={language} 
        onLanguageChange={changeLanguage} 
        content={currentContent} 
      />

      {/* Hero Section */}
      <section className="projects-hero">
        <div className="container">
          <div className="hero-content">
            <h1 className="hero-title">{currentContent.hero.title}</h1>
            <p className="hero-subtitle">{currentContent.hero.subtitle}</p>
          </div>
        </div>
      </section>

      <div className="container">
        <div className="projects-content">
          {/* Sidebar with filters */}
          <aside className="projects-sidebar">
            <div className="sidebar-section search-section">
              <h3>{currentContent.filters.search}</h3>
              <form onSubmit={handleSearchSubmit} className="search-form">
                <input
                  type="text"
                  placeholder={currentContent.filters.searchPlaceholder}
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="search-input"
                />
                <button type="submit" className="search-btn">
                  <svg viewBox="0 0 24 24" fill="none">
                    <path
                      d="M21 21L16.514 16.506L21 21ZM19 10.5C19 15.194 15.194 19 10.5 19C5.806 19 2 15.194 2 10.5C2 5.806 5.806 2 10.5 2C15.194 2 19 5.806 19 10.5Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </form>
            </div>


            <div className="sidebar-section">
              <h3>{currentContent.filters.year}</h3>
              <select
                value={filters.year}
                onChange={(e) => handleFilterChange('year', e.target.value)}
                className="filter-select"
              >
                <option value="">{currentContent.filters.allYears}</option>
                {years.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>

            {(filters.year || filters.search) && (
              <div className="sidebar-section">
                <button onClick={clearFilters} className="clear-filters-btn">
                  {currentContent.filters.clearFilters}
                </button>
              </div>
            )}
          </aside>

          {/* Main content area */}
          <main className="projects-main">
            {loading ? (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <p>{currentContent.messages.loading}</p>
              </div>
            ) : error ? (
              <div className="error-state">
                <svg viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                  <line x1="15" y1="9" x2="9" y2="15" stroke="currentColor" strokeWidth="2"/>
                  <line x1="9" y1="9" x2="15" y2="15" stroke="currentColor" strokeWidth="2"/>
                </svg>
                <h3>{currentContent.messages.error}</h3>
                <p>{error}</p>
                <button onClick={loadProjects} className="retry-btn">{currentContent.buttons.retry}</button>
              </div>
            ) : projects.length === 0 ? (
              <div className="empty-state">
                <svg viewBox="0 0 24 24" fill="none">
                  <path
                    d="M9 17H7C4.79086 17 3 15.2091 3 13V7C3 4.79086 4.79086 3 7 3H17C19.2091 3 21 4.79086 21 7V13C21 15.2091 19.2091 17 17 17H15"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M9 21H15"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <h3>{currentContent.messages.noResults}</h3>
                <p>{currentContent.messages.noResultsDescription}</p>
                <button onClick={clearFilters} className="clear-filters-btn">
                  {currentContent.filters.clearFilters}
                </button>
              </div>
            ) : (
              <>
                {/* Results header */}
                <div className="results-header">
                  <div className="results-info">
                    <h2>
                      {currentContent.messages.foundProjects} {pagination.total || 0} {currentContent.messages.projects}
                      {filters.year && ` ${currentContent.messages.fromYear} ${filters.year}. ${currentContent.messages.year}`}
                      {filters.search && ` ${currentContent.messages.forSearch} "${filters.search}"`}
                    </h2>
                  </div>
                </div>

                {/* Projects grid */}
                <div className="projects-grid">
                  {projects.map(project => {
                    const localizedProject = getLocalizedProject(project, language);
                    return (
                      <Link
                        key={project._id}
                        to={`/projekti/${project._id}`}
                        className="project-card"
                      >
                        <div className="project-image">
                          <img
                            src={project.gallery?.[0]?.url || '/images/placeholder/project-placeholder.jpg'}
                            alt={safeRender(localizedProject.localizedTitle, language)}
                            loading="lazy"
                          />
                          {project.featured && (
                            <div className="featured-badge">
                              <svg viewBox="0 0 24 24" fill="none">
                                <path
                                  d="M12 2L15.09 8.26L22 9L17 14L18.18 21L12 17.77L5.82 21L7 14L2 9L8.91 8.26L12 2Z"
                                  fill="currentColor"
                                />
                              </svg>
                            </div>
                          )}
                        </div>
                        <div className="project-content">
                          <div className="project-meta">
                            {project.completionDate && (
                              <span className="project-date">{formatDate(project.completionDate)}</span>
                            )}
                          </div>
                          <h3 className="project-title">{safeRender(localizedProject.localizedTitle, language)}</h3>

                          {(() => {
                            const descriptionText = safeRender(localizedProject.localizedDescription, language);
                            return descriptionText && !descriptionText.includes('Missing') && !descriptionText.includes('translation') ? (
                              <p className="project-description">
                                {descriptionText.length > 120
                                  ? `${descriptionText.substring(0, 120)}...`
                                  : descriptionText
                                }
                              </p>
                            ) : null;
                          })()}

                          {(() => {
                            const clientText = safeRender(localizedProject.localizedClient, language);
                            return clientText && !clientText.includes('Missing') && !clientText.includes('translation') ? (
                              <div className="project-client">
                                <span>{currentContent.messages.client}: {clientText}</span>
                              </div>
                            ) : null;
                          })()}

                          {(() => {
                            const locationText = safeRender(localizedProject.localizedLocation, language);
                            return locationText && !locationText.includes('Missing') && !locationText.includes('translation') ? (
                              <div className="project-location">
                                <svg viewBox="0 0 24 24" fill="none">
                                  <path
                                    d="M21 10C21 17 12 23 12 23S3 17 3 10C3 5.02944 7.02944 1 12 1C16.9706 1 21 5.02944 21 10Z"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                  />
                                  <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="2"/>
                                </svg>
                                <span>{locationText}</span>
                              </div>
                            ) : null;
                          })()}
                        </div>
                      </Link>
                    );
                  })}
                </div>

                {/* Pagination */}
                {pagination.pages > 1 && (
                  <div className="pagination">
                    <button
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page <= 1}
                      className="pagination-btn"
                    >
                      <svg viewBox="0 0 24 24" fill="none">
                        <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      {currentContent.pagination.previous}
                    </button>

                    <div className="pagination-numbers">
                      {Array.from({ length: pagination.pages }, (_, i) => i + 1)
                        .filter(page => {
                          const current = pagination.page;
                          return page === 1 || page === pagination.pages || 
                                 (page >= current - 2 && page <= current + 2);
                        })
                        .map((page, index, array) => (
                          <React.Fragment key={page}>
                            {index > 0 && array[index - 1] !== page - 1 && (
                              <span className="pagination-dots">...</span>
                            )}
                            <button
                              onClick={() => handlePageChange(page)}
                              className={`pagination-number ${page === pagination.page ? 'active' : ''}`}
                            >
                              {page}
                            </button>
                          </React.Fragment>
                        ))
                      }
                    </div>

                    <button
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page >= pagination.pages}
                      className="pagination-btn"
                    >
                      {currentContent.pagination.next}
                      <svg viewBox="0 0 24 24" fill="none">
                        <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  </div>
                )}
              </>
            )}
          </main>
        </div>
      </div>

      {/* Footer Section */}
      <Footer content={currentContent} />
    </div>
  );
};

export default ProjectsPage;